import React, { useEffect, useState, useRef } from 'react';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import HolidayService from '../../services/holidayService';
import { showConfirm, showSuccessDetail, showDeleteDetail, showError, showLoading, hideLoading } from '../../components/utils/swal-helpers';
import FormDatePicker from '../../components/common/form-date-picker';

// ─────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────

const ROWS_PER_PAGE = 8;

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────

/** แปลง ISO date string → DD/MM/YYYY */
const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const datePart = dateStr.split('T')[0]; // "2026-01-01"
    if (!datePart) return '-';
    const [yyyy, mm, dd] = datePart.split('-');
    return `${dd}/${mm}/${yyyy}`;
};

/** แปลง Date object → ISO string YYYY-MM-DDT00:00:00 สำหรับส่ง API */
const toApiDateStr = (date) => {
    if (!date) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00`;
};

/** คำนวณ page numbers ที่แสดงรอบ current page (±2) */
const getPageNumbers = (currentPage, totalPages) => {
    const start = Math.max(1, currentPage - 2);
    const end   = Math.min(totalPages, currentPage + 2);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

// ─────────────────────────────────────────────────────────
// Holiday Modal
// ─────────────────────────────────────────────────────────

const HolidayModal = ({ mode, data, onClose, onSave }) => {
    const [form, setForm] = useState({
        name_th: data?.name_th || '',
        in_date: data?.in_date ? new Date(data.in_date) : null,
        in_year: data?.in_year || new Date().getFullYear(),
    });

    const isEdit = mode === 'edit';

    const handleChange = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    const handleDateChange = (date) => {
        // sync in_year อัตโนมัติเมื่อเลือกวันที่
        handleChange('in_date', date);
        handleChange('in_year', date ? date.getFullYear() : new Date().getFullYear());
    };

    const handleSave = () => {
        // setTimeout เพื่อให้ state flush ก่อน onSave
        setTimeout(() => onSave(form), 0);
    };

    return (
        <div className="mc-modal-overlay" onClick={onClose}>
            <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
                <div className="mc-modal-header">
                    <h3>{isEdit ? 'แก้ไข Edit' : 'เพิ่มวันหยุดประจำปี New annual holiday'}</h3>
                </div>
                <div className="mc-modal-body">
                    <div className="border-line">
                        <div className="mc-form-row">
                            <div className="mc-form-group">
                                <label>Date</label>
                                <FormDatePicker
                                    value={form.in_date}
                                    onChange={handleDateChange}
                                />
                            </div>
                            <div className="mc-form-group">
                                <label>Holiday Name</label>
                                <input
                                    type="text"
                                    placeholder="ชื่อวันหยุด"
                                    value={form.name_th}
                                    onChange={(e) => handleChange('name_th', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mc-modal-footer">
                    <button className="mc-btn mc-btn--cancel" onClick={onClose}>Cancel</button>
                    <button className="mc-btn mc-btn--save" onClick={handleSave}>
                        {isEdit ? 'Save' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────

const AdminHoliday = () => {
    // ── Data state ──
    const [data, setData]     = useState([]);
    const [search, setSearch] = useState('');
    const [modal, setModal]   = useState(null); // null | { mode, data? }
    const [newId, setNewId]   = useState(null); // id ของ row ที่เพิ่งสร้าง (สำหรับ highlight)

    // ── Pagination state ──
    const [currentPage, setCurrentPage] = useState(1);

    // ── Refs ──
    const highlightRef = useRef(null); // ref สำหรับ clear timeout highlight

    // โหลดข้อมูลตอน mount
    useEffect(() => {
        loadData();
        // clear timeout เมื่อ unmount
        return () => { if (highlightRef.current) clearTimeout(highlightRef.current); };
    }, []);

    // ─────────────────────────────────────────────────────
    // Data loaders
    // ─────────────────────────────────────────────────────

    const loadData = async () => {
        try {
            const resp = await HolidayService.SearchHolidays({ status: 1 });
            if (!resp.data.is_success || !resp.data.result) return;
            // console.log('raw result:', resp.data.result.map(d => ({ id: d.id, name: d.name_th, date: d.in_date })));
            const sorted = [...resp.data.result].sort(
                (a, b) => new Date(a.in_date) - new Date(b.in_date)
            );
            // console.log('sorted:', JSON.stringify(sorted.map(d => ({ id: d.id, name: d.name_th, date: d.in_date }))));
            setData(sorted);
        } catch (err) { console.error(err); }
    };

    // ─────────────────────────────────────────────────────
    // Handlers
    // ─────────────────────────────────────────────────────

    const handleSave = async (form) => {
        const { mode: currentMode, data: currentData } = modal;
        setModal(null);

        const confirmed = await showConfirm({
            title: currentMode === 'edit' ? 'ยืนยันการแก้ไขวันหยุด' : 'ยืนยันการเพิ่มวันหยุดประจำปีใหม่',
            detail: 'กด Confirm เพื่อดำเนินการต่อ',
        });
        if (!confirmed.isConfirmed) return;

        try {
            showLoading();
            const payload = {
                name_th: form.name_th,
                name_en: form.name_th,
                in_date: toApiDateStr(form.in_date),
                in_year: form.in_year,
                status:  1,
            };

            let createdId = null;
            console.log('in_date value:', form.in_date);
            console.log('toApiDateStr:', toApiDateStr(form.in_date));
            console.log('payload:', payload);
            if (currentMode === 'edit') {
                await HolidayService.UpdateHoliday(currentData?.id, payload);
            } else {
                const resp = await HolidayService.CreateHoliday(payload);
                createdId = resp.data?.result?.id || resp.data?.result?.[0]?.id || null;
            }

            hideLoading();
            await showSuccessDetail({
                icon: 'mdi mdi-check-circle',
                title: currentMode === 'edit' ? 'แก้ไขสำเร็จแล้ว' : 'เพิ่มวันหยุดประจำปีสำเร็จแล้ว',
            });

            await loadData();

            // ─── reset ไปหน้าแรกหลัง create เพื่อให้ scroll หา row ใหม่ตามวันที่ได้ ───
            if (currentMode === 'add') setCurrentPage(1);

            // highlight row ที่เพิ่งสร้างใหม่เป็นเวลา 10 วิ
            if (currentMode === 'add' && createdId) {
                setNewId(createdId);
                if (highlightRef.current) clearTimeout(highlightRef.current);
                highlightRef.current = setTimeout(() => setNewId(null), 10000);
            }
        } catch (err) { hideLoading(); showError(); }
    };

    const handleDelete = async (row) => {
        const confirmed = await showConfirm({
            title: 'แน่ใจใช่ไหมว่าต้องการลบวันหยุดประจำปี',
            detail: 'การลบจะไม่สามารถย้อนกลับได้',
        });
        if (!confirmed.isConfirmed) return;
        try {
            showLoading();
            await HolidayService.DeleteHoliday(row.id);
            hideLoading();
            await showDeleteDetail({ title: 'ลบวันหยุดออกแล้ว' });
            loadData();
        } catch (err) { hideLoading(); showError(); }
    };

    // ─────────────────────────────────────────────────────
    // Derived data (filter + pagination)
    // ─────────────────────────────────────────────────────

    const filtered = data.filter((row) => {
        const q = search.toLowerCase();
        if (!q) return true;
        return (
            (row.name_th || '').toLowerCase().includes(q) ||
            (row.name_en || '').toLowerCase().includes(q) ||
            formatDate(row.in_date).includes(q)
        );
    });

    const totalPages  = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
    const safePage    = Math.min(currentPage, totalPages);
    const paged       = filtered.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);
    const pageNumbers = getPageNumbers(safePage, totalPages);

    const goPage = (p) => {
        if (p >= 1 && p <= totalPages) setCurrentPage(p);
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1); // reset หน้าเมื่อ search เปลี่ยน
    };

    // ─────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────

    const countLabel = filtered.length === 0
        ? '0 รายการ'
        : `${(safePage - 1) * ROWS_PER_PAGE + 1}-${Math.min(safePage * ROWS_PER_PAGE, filtered.length)} จาก ${filtered.length} รายการ`;

    return (
        <>
            <div className="mc-page">
                {/* ── Header ── */}
                <div className="mc-header">
                    <div className="mc-header__left">
                        <h2 className="mc-title">Master Data - Annual Holiday Setup</h2>
                        <p className="mc-subtitle">ตั้งค่าวันหยุดประจำปี</p>
                    </div>
                    <button className="mc-btn mc-btn--new" onClick={() => setModal({ mode: 'add' })}>
                        <i className="ri-add-line" /> New Holiday
                    </button>
                </div>

                <div className="mc-card">
                    {/* ── Toolbar: search + count ── */}
                    <div className="mc-toolbar">
                        <div className="mc-search">
                            <i className="ri-search-line" />
                            <input
                                type="text"
                                placeholder="Select"
                                value={search}
                                onChange={handleSearchChange}
                            />
                        </div>
                        <div className="mc-toolbar__right">
                            <span className="mc-count">{countLabel}</span>
                        </div>
                    </div>

                    {/* ── Table ── */}
                    <div style={{ overflowX: 'auto' }}>
                        <table className="mc-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 60 }}>No.</th>
                                    <th style={{ width: 120 }}>Date</th>
                                    <th>Holiday Name</th>
                                    <th style={{ width: 100, textAlign: 'center' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="mc-table-empty">ไม่มีข้อมูล</td>
                                    </tr>
                                ) : (
                                    paged.map((row, idx) => (
                                        <tr key={row.id} className={row.id === newId ? 'mc-row-new' : ''}>
                                            <td className="mc-col-no">{(safePage - 1) * ROWS_PER_PAGE + idx + 1}</td>
                                            <td className="mc-col-muted">{formatDate(row.in_date)}</td>
                                            <td>{row.name_th}</td>
                                            <td>
                                                <div className="mc-actions">
                                                    <button
                                                        className="mc-action-btn mc-action-btn--edit"
                                                        onClick={() => setModal({ mode: 'edit', data: row })}
                                                    >
                                                        <i className="ri-pencil-line" />
                                                    </button>
                                                    <button
                                                        className="mc-action-btn mc-action-btn--delete"
                                                        onClick={() => handleDelete(row)}
                                                    >
                                                        <i className="ri-delete-bin-line" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Pagination ── */}
                    <div className="table-pagination">
                        <Pagination>
                            <PaginationItem disabled={safePage === 1}>
                                <PaginationLink first onClick={() => goPage(1)} />
                            </PaginationItem>
                            <PaginationItem disabled={safePage === 1}>
                                <PaginationLink previous onClick={() => goPage(safePage - 1)} />
                            </PaginationItem>
                            {pageNumbers.map((p) => (
                                <PaginationItem key={p} active={p === safePage}>
                                    <PaginationLink onClick={() => goPage(p)}>{p}</PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={safePage === totalPages}>
                                <PaginationLink next onClick={() => goPage(safePage + 1)} />
                            </PaginationItem>
                            <PaginationItem disabled={safePage === totalPages}>
                                <PaginationLink last onClick={() => goPage(totalPages)} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                </div>
            </div>

            {/* ── Modal ── */}
            {modal && (
                <HolidayModal
                    mode={modal.mode}
                    data={modal.data}
                    onClose={() => setModal(null)}
                    onSave={handleSave}
                />
            )}
        </>
    );
};

export default AdminHoliday;